"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import posthog from "posthog-js";
import { safeCapture } from "@/lib/analytics/safeCapture";
import { reloadOnce } from "@/lib/recovery/reloadOnce";
import { TURNSTILE_UNAVAILABLE_MESSAGE } from "./messages";

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
const TOKEN_TIMEOUT_MS = 12_000;
// Deadline once Cloudflare escalates to a visible interactive challenge (checkbox).
// TOKEN_TIMEOUT_MS assumes an invisible pass; a visible checkbox needs the user to
// notice and click it, and 12s killed (and reset) challenges mid-solve in the
// 31 Aug 2026 incident (TRA-316) — every escalated quiz start timed out. Armed as
// a fresh window from the moment the challenge turns interactive, and used for any
// getToken() call that joins an already-interactive challenge.
const INTERACTIVE_TIMEOUT_MS = 60_000;
// How long a mounted widget gets to report onWidgetLoad (i.e. to have actually rendered)
// before it is declared dead: fast-fail later getToken() calls, report, and reload.
// Deliberately generous. A first cut used 4s and also used it as the getToken() deadline,
// which removed the 12s grace a slow-but-healthy widget always had; production data
// (45 days of successful invisible quiz starts) showed 22% still unsolved at 4s and 40% of
// mobile sessions, so a 4s ceiling would have hard-failed (and reloaded) healthy mobile
// starts. 10s is under the old 12s ceiling yet well past where healthy widgets land.
// Measures *render* time, never solve time: onWidgetLoad fires at render, long before any
// escalation, so this is always disarmed before a checkbox could appear and cannot
// interfere with TRA-316's interactive window. The first solve after each load reports
// `ready_ms` so this can be re-tuned from real widget-ready times.
const WIDGET_DEAD_TIMEOUT_MS = 10_000;
// Own key, so a dead-widget reload never spends the chunk-load recovery's cooldown
// (lib/chunks/chunkErrors.ts) or vice versa.
const TURNSTILE_RELOAD_MARKER_KEY = "bsa_turnstile_reload_at";
// Turnstile tokens live ~300 s server-side, but the widget's onExpire callback
// never fires while iOS Safari has the tab suspended — so a resumed tab can
// hold a token that is long dead. Treat anything older than this as stale and
// re-execute the widget instead of replaying it (which the proxy would reject
// as "Bot check failed"). Kept well under 300 s to leave round-trip margin.
// Exported so resume handling (usePageResume in QuizWizard) can align its
// hidden-time threshold with this window instead of resetting fresh tokens.
export const TOKEN_MAX_AGE_MS = 120_000;

/** Which gated surface is mounting the widget — instrumentation only, never sent upstream. */
export type TurnstileSurface = "quiz" | "auth" | "enquiry" | "campfire";

// Which deadline a waiter's timer is currently armed at. Decides the reject message
// and telemetry reason when it fires, and lets handleWidgetLoad find the waiters that
// are still waiting on a widget that hasn't rendered yet.
type WaiterStage = "unready" | "base" | "interactive";

// A getToken() call awaiting the next onSuccess. `timer` is its deadline handle,
// kept on the object (not in the promise closure) so handleBeforeInteractive can
// clear and re-arm it with the interactive window when a checkbox appears.
type Waiter = {
  resolve: (t: string) => void;
  reject: (e: Error) => void;
  timer?: ReturnType<typeof setTimeout>;
  stage: WaiterStage;
};

/**
 * Zero-render sibling of <Turnstile> whose only job is to give the hook a mount /
 * unmount signal for the *widget subtree* — not for the hook's owner. QuizWizard
 * deliberately unmounts the widget during steps 1-2 while the hook stays alive, so
 * arming the readiness watchdog from the hook itself would fire falsely there.
 *
 * Rendered FIRST in the fragment so its effect runs before <Turnstile>'s own
 * (sibling effects run in tree order) — otherwise a synchronous onWidgetLoad could
 * land before this resets `readyRef`, and the reset would then clobber it.
 */
function WidgetLifecycle({ onMount }: { onMount: () => () => void }) {
  useEffect(onMount, [onMount]);
  return null;
}

export type UseTurnstileTokenResult = {
  /** Mount this node somewhere in your render tree (invisible when no challenge needed).
   *  Must never be mounted inside `display:none` / `className="hidden"` — an escalated
   *  interactive checkbox would be unclickable. Do NOT pass an `options.size` alongside
   *  this hook's execute mode — in execute mode the library pins the container at
   *  {width:0, height:0, overflow:hidden} whenever `size` is set, even after execute(). */
  widget: ReactNode;
  /**
   * Resolves with the current Turnstile token (or an empty string when no site key
   * is configured). Triggers the widget's `execute()` on a cache miss and awaits
   * onSuccess. Rejects after TOKEN_TIMEOUT_MS — extended to INTERACTIVE_TIMEOUT_MS
   * once a visible interactive challenge is shown — or immediately on onError /
   * onExpire.
   */
  getToken: () => Promise<string>;
  /** Force a fresh token. Call after each successful gated request so the next attempt
   *  gets a new token (Turnstile tokens are single-use, ~300 s TTL). Rejects any
   *  in-flight getToken() waiter as cancelled rather than leaving it to time out. */
  reset: () => void;
  /** True while an escalated visible challenge (checkbox) is awaiting user input.
   *  Use it to tell the user to complete the check ("please confirm you're human")
   *  instead of showing loading copy that buries the checkbox. */
  interactive: boolean;
};

/**
 * Provides an invisible/interaction-only Turnstile widget and a `getToken()` helper
 * suitable for gating form submits or background API calls.
 *
 * Runs in `execution: "execute"` mode — mounting the widget is completely inert. A
 * challenge only ever runs as the direct result of a `getToken()` call, i.e. a user
 * action against a gated endpoint (quiz start, login, signup, enquiry submit). This
 * also means `refreshExpired` is "manual" — Cloudflare will not reissue an expired
 * token on its own; `handleExpire` below resets the widget so the next `getToken()`
 * call can `execute()` cleanly (this is the pattern the library's own docs prescribe
 * for "manual" — a bare execute() on an expired-but-unreset widget is not reliable).
 *
 * When NEXT_PUBLIC_TURNSTILE_SITE_KEY is unset, the widget renders nothing and
 * getToken() resolves with "" immediately — so local dev / key-less envs are unaffected.
 */
export function useTurnstileToken({
  surface,
}: {
  surface: TurnstileSurface;
}): UseTurnstileTokenResult {
  const widgetRef = useRef<TurnstileInstance | null>(null);
  // Holds the most-recently-issued token (cleared on use / reset / expire / error).
  const tokenRef = useRef<string | null>(null);
  // When tokenRef was issued (ms epoch); 0 = no token.
  const issuedAtRef = useRef(0);
  // True once the widget has rendered and execute() is safe to call.
  const readyRef = useRef(false);
  // True while a challenge is in flight, so concurrent getToken() callers share it
  // instead of each calling execute() again. Every path that can end the challenge
  // (success, error, expire-then-reset, timeout, cancel-via-reset) MUST clear this —
  // otherwise execute() below permanently no-ops and every future getToken() hangs
  // to its timeout with no challenge ever dispatched.
  const executingRef = useRef(false);
  // True once a mounted widget failed to report ready within WIDGET_DEAD_TIMEOUT_MS.
  // Makes getToken() fail fast instead of queueing a waiter that can only expire.
  // Cleared by a late onWidgetLoad (a slow-but-alive widget recovers) and on remount.
  const deadRef = useRef(false);
  // Handle for the mount watchdog, so onWidgetLoad / unmount can disarm it.
  const readyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True once the user has typed or changed anything since this widget mounted. Gates
  // the auto-reload only — never the telemetry or the fast-fail — so a reload can
  // never wipe input the user has already started entering.
  const engagedRef = useRef(false);
  // When the current widget subtree mounted, and how long it then took to report ready.
  // readyMsRef is consumed by the first solve after a load (see handleSuccess).
  const mountedAtRef = useRef(0);
  const readyMsRef = useRef<number | null>(null);
  // Queue of waiters awaiting the next onSuccess.
  const waitersRef = useRef<Waiter[]>([]);
  // True while a visible interactive challenge awaits user input. State drives the
  // returned `interactive` flag (UI copy); the ref is for reads inside the stable
  // callbacks below, where the state value would be stale. Kept in sync by
  // setInteractive only.
  const [interactive, setInteractiveState] = useState(false);
  const interactiveRef = useRef(false);

  const setInteractive = useCallback((value: boolean) => {
    interactiveRef.current = value;
    setInteractiveState(value);
  }, []);

  const resolveWaiters = useCallback((token: string) => {
    executingRef.current = false;
    const waiters = waitersRef.current.splice(0);
    for (const w of waiters) {
      clearTimeout(w.timer);
      w.resolve(token);
    }
  }, []);

  const rejectWaiters = useCallback((err: Error) => {
    executingRef.current = false;
    const waiters = waitersRef.current.splice(0);
    for (const w of waiters) {
      clearTimeout(w.timer);
      w.reject(err);
    }
  }, []);

  // (Re-)arms a waiter's deadline. Called with TOKEN_TIMEOUT_MS on the invisible
  // path, INTERACTIVE_TIMEOUT_MS when a checkbox is (or becomes) visible, and
  // TOKEN_TIMEOUT_MS with the "unready" stage while the widget hasn't rendered yet.
  const armWaiterTimeout = useCallback(
    (waiter: Waiter, ms: number, stage: WaiterStage) => {
      waiter.stage = stage;
      waiter.timer = setTimeout(() => {
        // Remove this specific waiter so it doesn't double-fire if onSuccess/onError
        // fires after the timeout (e.g. Turnstile resolves just after the deadline).
        waitersRef.current = waitersRef.current.filter((w) => w !== waiter);
        // Only clear executingRef / reset once nothing else is still waiting on the
        // same in-flight challenge — otherwise a second concurrent getToken() call
        // would have its own execute() call fired from underneath it. Once the queue
        // is empty, this MUST reset both, or every later getToken() finds
        // executingRef stuck true, calls execute() nowhere, and times out forever.
        if (waitersRef.current.length === 0) {
          executingRef.current = false;
          widgetRef.current?.reset();
          // reset() destroys any visible checkbox, so the interactive copy must
          // revert with it.
          setInteractive(false);
        }
        if (stage === "unready") {
          // No challenge was ever dispatched (the widget never rendered), so this is
          // not a "timeout" — reporting it as one would put widget-load failures in
          // the same bucket as genuinely slow challenges.
          safeCapture("turnstile_challenge_failed", {
            surface,
            reason: "widget_unavailable",
          });
          waiter.reject(new Error(TURNSTILE_UNAVAILABLE_MESSAGE));
          return;
        }
        posthog.capture("turnstile_challenge_failed", { surface, reason: "timeout" });
        waiter.reject(new Error("Security check timed out. Please try again."));
      }, ms);
    },
    [setInteractive, surface],
  );

  const handleSuccess = useCallback(
    (token: string) => {
      tokenRef.current = token;
      issuedAtRef.current = Date.now();
      setInteractive(false);
      // ready_ms rides on the first solve after each load only: later solves reuse the
      // same widget, so the same value would misstate them.
      const readyMs = readyMsRef.current;
      readyMsRef.current = null;
      posthog.capture(
        "turnstile_challenge_solved",
        readyMs === null ? { surface } : { surface, ready_ms: readyMs },
      );
      resolveWaiters(token);
    },
    [resolveWaiters, setInteractive, surface],
  );

  const handleError = useCallback(
    (code: string) => {
      tokenRef.current = null;
      issuedAtRef.current = 0;
      setInteractive(false);
      // error_code is Cloudflare's numeric string (e.g. "110600" challenge timed
      // out, "110620" interaction timed out) — kept on the event so escalation
      // failures are distinguishable in PostHog.
      posthog.capture("turnstile_challenge_failed", {
        surface,
        reason: "error",
        error_code: code,
      });
      rejectWaiters(
        new Error("Security check failed. Please reload the page and try again."),
      );
    },
    [rejectWaiters, setInteractive, surface],
  );

  const handleExpire = useCallback(() => {
    tokenRef.current = null;
    issuedAtRef.current = 0;
    setInteractive(false);
    // Expiry at ~300s is normal lifecycle on a long-lived mount, not a failure — only
    // emit the failure event when a getToken() call was actually waiting on this
    // widget (i.e. a user action got interrupted by it), not on a background expiry
    // nobody was blocked on.
    if (waitersRef.current.length > 0) {
      posthog.capture("turnstile_challenge_failed", { surface, reason: "expired" });
    } else {
      posthog.capture("turnstile_challenge_expired", { surface });
    }
    // refreshExpired: "manual" means Cloudflare will NOT reissue a token on its own —
    // reset the widget now so a subsequent getToken()'s execute() call actually gets a
    // fresh challenge instead of hanging on an expired-but-unreset widget.
    widgetRef.current?.reset();
    executingRef.current = false;
    rejectWaiters(new Error("Security check expired. Please try again."));
  }, [rejectWaiters, setInteractive, surface]);

  const handleBeforeInteractive = useCallback(() => {
    // A late escalation callback can arrive just after the last waiter's own
    // deadline fired and drained the queue — which already reset the widget,
    // destroying this challenge. Nothing is waiting and there is no checkbox to
    // point the user at, so neither the interactive flag nor the _shown capture
    // should fire (a stale flag would arm the next getToken() at the interactive
    // window and swap the splash copy with nothing to click).
    if (waitersRef.current.length === 0) return;
    posthog.capture("turnstile_challenge_shown", { surface });
    setInteractive(true);
    // The deadlines armed so far assumed an invisible pass — a visible checkbox
    // needs the user to notice and click it, so give every pending waiter a fresh
    // interactive window instead of letting the short deadline reset the widget
    // (destroying the checkbox) mid-solve. Repeat escalations deliberately re-arm
    // from zero each time: a challenge actively in front of the user is worth
    // waiting for, and Cloudflare's own onTimeout is the terminal backstop.
    for (const w of waitersRef.current) {
      clearTimeout(w.timer);
      armWaiterTimeout(w, INTERACTIVE_TIMEOUT_MS, "interactive");
    }
  }, [armWaiterTimeout, setInteractive, surface]);

  const handleAfterInteractive = useCallback(() => {
    // The visible checkbox is gone (user interacted); revert the UI copy. Waiter
    // deadlines already armed at the interactive window keep running as the
    // backstop for token issuance.
    setInteractive(false);
  }, [setInteractive]);

  const handleTimeout = useCallback(() => {
    // Cloudflare's own challenge lifetime elapsed without the user solving it.
    // The library docs prescribe a reset here; doing it (and rejecting) also keeps
    // executingRef from being stranded true by a Cloudflare-initiated timeout.
    tokenRef.current = null;
    issuedAtRef.current = 0;
    setInteractive(false);
    widgetRef.current?.reset();
    posthog.capture("turnstile_challenge_failed", { surface, reason: "timeout" });
    rejectWaiters(new Error("Security check timed out. Please try again."));
  }, [rejectWaiters, setInteractive, surface]);

  const handleWidgetLoad = useCallback(() => {
    readyRef.current = true;
    deadRef.current = false;
    readyMsRef.current = mountedAtRef.current > 0 ? Date.now() - mountedAtRef.current : null;
    if (readyTimerRef.current) {
      clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
    // A waiter still waiting on a widget that hadn't rendered now has a real
    // widget to wait on — give it the full invisible-pass deadline (or the interactive
    // one if a checkbox is already up). Same re-arm idiom as handleBeforeInteractive,
    // and only "unready" waiters: a late onWidgetLoad (e.g. a remount mid-escalation)
    // must never downgrade a 60s interactive waiter back to 12s.
    for (const w of waitersRef.current) {
      if (w.stage !== "unready") continue;
      clearTimeout(w.timer);
      if (interactiveRef.current) armWaiterTimeout(w, INTERACTIVE_TIMEOUT_MS, "interactive");
      else armWaiterTimeout(w, TOKEN_TIMEOUT_MS, "base");
    }
  }, [armWaiterTimeout]);

  // Mount watchdog. Armed when the widget subtree mounts (see WidgetLifecycle), NOT
  // when getToken() is called: getToken() only runs on submit, i.e. after the user has
  // typed credentials, and a reload then would destroy their input. Detecting a dead
  // widget a few seconds after it mounts means recovery almost always happens before
  // anything is typed.
  //
  // The failure it guards against: @marsidev/react-turnstile calls onWidgetLoad only
  // after a successful window.turnstile.render(), and if render() returns undefined
  // neither onWidgetLoad nor onError ever fires; and its api.js load state is
  // module-level, so a script that never executes wedges every widget in the document
  // until a full reload (client-side navigation doesn't clear it). Without this, that
  // showed as an endless "Security check timed out" (21 Sep 2026).
  //
  // Deliberately NOT wired via <Turnstile scriptOptions={{ onError }}>: v1.6.1's
  // injector does `onError && (script.onerror = onError, delete window[onLoadCallback])`,
  // deleting the onload global it just installed, which wedges the module-level script
  // promise at "loading" forever — the very failure we're fixing. Instead a missing
  // window.turnstile at the deadline is reported as reason "script_unavailable".
  const handleWidgetMount = useCallback(() => {
    readyRef.current = false;
    deadRef.current = false;
    engagedRef.current = false;
    mountedAtRef.current = Date.now();
    readyMsRef.current = null;

    const markEngaged = () => {
      engagedRef.current = true;
    };
    // Typing is not the only sign someone is using the page: on the modal surfaces a
    // reader who opened the form, scrolled and clicked around without typing yet would
    // otherwise get the page reloaded (and the modal closed) under them. Capture phase so
    // scroll, which doesn't bubble, is still seen on `document`. The click that *opened*
    // a modal doesn't count: this resets on widget mount, which comes after it.
    const engagementEvents = ["keydown", "input", "change", "pointerdown", "scroll"] as const;
    for (const type of engagementEvents) {
      document.addEventListener(type, markEngaged, {
        capture: true,
        passive: true,
      });
    }

    const check = () => {
      readyTimerRef.current = null;
      if (readyRef.current) return; // raced with onWidgetLoad

      // Background tabs get throttled timers, and reloading a tab nobody is looking at
      // is hostile — defer the verdict until it's visible again.
      if (document.visibilityState !== "visible") {
        readyTimerRef.current = setTimeout(check, WIDGET_DEAD_TIMEOUT_MS);
        return;
      }

      deadRef.current = true;
      const reason =
        typeof (window as unknown as { turnstile?: unknown }).turnstile === "undefined"
          ? "script_unavailable"
          : "widget_dead";
      const engaged = engagedRef.current;

      // Queued waiters are deliberately NOT drained here: each keeps its own 12s deadline,
      // so a widget that renders at, say, 11s still beats the pre-watchdog behaviour. This
      // only stops *later* getToken() calls from queueing behind a dead widget.

      // The one and only place a reload is triggered. Never from a waiter deadline:
      // by then getToken() has run, and a reload there could navigate a user away
      // mid-solve (the TRA-316 failure mode).
      const reloaded = engaged
        ? false
        : reloadOnce({
            markerKey: TURNSTILE_RELOAD_MARKER_KEY,
            onBeforeReload: () =>
              safeCapture("turnstile_widget_unavailable", {
                surface,
                reason,
                engaged,
                reloaded: true,
              }),
          });
      if (!reloaded) {
        safeCapture("turnstile_widget_unavailable", {
          surface,
          reason,
          engaged,
          reloaded: false,
        });
      }
    };
    readyTimerRef.current = setTimeout(check, WIDGET_DEAD_TIMEOUT_MS);

    return () => {
      if (readyTimerRef.current) {
        clearTimeout(readyTimerRef.current);
        readyTimerRef.current = null;
      }
      for (const type of engagementEvents) {
        document.removeEventListener(type, markEngaged, { capture: true });
      }
      // The widget is gone, so it is no longer ready — without this, a remount saw a
      // stale readyRef=true and took the ready branch against a widget that hadn't
      // rendered yet. Deliberately does NOT touch executingRef / waitersRef:
      // QuizWizard unmounts and remounts the widget mid-challenge, and its recovery
      // (the onWidgetLoad flush) depends on those surviving.
      readyRef.current = false;
      deadRef.current = false;
      mountedAtRef.current = 0;
      readyMsRef.current = null;
    };
  }, [surface]);

  const execute = useCallback(() => {
    if (executingRef.current) return;
    executingRef.current = true;
    if (readyRef.current && widgetRef.current) {
      widgetRef.current.execute();
    }
    // If not ready (or the widget has been unmounted, e.g. QuizWizard swapping the
    // splash/error-card subtree it lives in), this is a no-op for now.
    // Turnstile.execute() itself only ever console.warns on a not-ready widget — it
    // never throws — so there is nothing to catch here. handleWidgetLoad's flush
    // below (fired by the library right after any (re)render of the widget) is what
    // dispatches this once a widget instance actually exists; getToken()'s deadline
    // (TOKEN_TIMEOUT_MS, or INTERACTIVE_TIMEOUT_MS once a visible challenge shows)
    // remains the backstop if no remount ever happens.
  }, []);

  const getToken = useCallback((): Promise<string> => {
    // No site key configured → no-op; pass empty string so proxy skip-verifies.
    if (!SITE_KEY) return Promise.resolve("");

    // Token already available and fresh (common path for second+ submit, or
    // fast-token flow).
    if (tokenRef.current && Date.now() - issuedAtRef.current < TOKEN_MAX_AGE_MS) {
      return Promise.resolve(tokenRef.current);
    }

    // Stale token (tab was suspended past the freshness window): discard it and
    // re-execute the widget, then fall through to await the next onSuccess.
    if (tokenRef.current) {
      tokenRef.current = null;
      issuedAtRef.current = 0;
      widgetRef.current?.reset();
    }

    // The widget is mounted but never rendered — a challenge can never be dispatched,
    // so don't queue a waiter that can only expire. Distinct message and reason from a
    // genuine timeout: "couldn't load" is actionable (reload), "timed out" is not.
    if (deadRef.current) {
      safeCapture("turnstile_challenge_failed", {
        surface,
        reason: "widget_unavailable",
      });
      return Promise.reject(new Error(TURNSTILE_UNAVAILABLE_MESSAGE));
    }

    // Await the next onSuccess callback.
    return new Promise<string>((resolve, reject) => {
      const waiter: Waiter = { resolve, reject, stage: "base" };
      const ready = readyRef.current && widgetRef.current !== null;
      if (!ready) {
        // Not rendered yet (or unmounted, e.g. QuizWizard mid-step). Same 12s a healthy
        // widget always had; the "unready" stage only changes what an expiry says (no
        // challenge was ever dispatched, so "couldn't load", not "timed out"). If
        // onWidgetLoad arrives it upgrades this waiter to a fresh deadline.
        armWaiterTimeout(waiter, TOKEN_TIMEOUT_MS, "unready");
      } else if (interactiveRef.current) {
        // A caller joining a challenge that has already escalated to a visible
        // checkbox gets the interactive window too, not the invisible-pass deadline.
        armWaiterTimeout(waiter, INTERACTIVE_TIMEOUT_MS, "interactive");
      } else {
        armWaiterTimeout(waiter, TOKEN_TIMEOUT_MS, "base");
      }
      waitersRef.current.push(waiter);
      execute();
    });
  }, [armWaiterTimeout, execute, surface]);

  const reset = useCallback(() => {
    tokenRef.current = null;
    issuedAtRef.current = 0;
    executingRef.current = false;
    setInteractive(false);
    widgetRef.current?.reset();
    // Don't leave a caller's getToken() hanging to its own timeout just because
    // something else (e.g. a modal closing) reset the widget out from under it.
    rejectWaiters(new Error("Security check cancelled."));
  }, [rejectWaiters, setInteractive]);

  // One DOM id per surface. Before this every widget used the library default
  // `cf-turnstile`, and QuizWizard's auth_required branch mounts a quiz-surface widget
  // and an auth-surface widget at the same time — two elements sharing an id, which is
  // invalid HTML and a plausible cause of render() returning undefined (the silent
  // dead widget the mount watchdog above exists to catch). Invariant: never mount two
  // widgets with the same `surface` simultaneously.
  const containerId = `cf-turnstile-${surface}`;

  const widget: ReactNode = SITE_KEY ? (
    <>
      <WidgetLifecycle onMount={handleWidgetMount} />
      <Turnstile
        id={containerId}
        ref={widgetRef}
        siteKey={SITE_KEY}
        options={{
          appearance: "interaction-only",
          execution: "execute",
          refreshExpired: "manual",
        }}
        onWidgetLoad={() => {
          handleWidgetLoad();
          // Flush a getToken() call that arrived before the widget finished (re)rendering
          // — e.g. QuizWizard remounting the widget into a new subtree. onWidgetLoad does
          // NOT fire on reset(), only on an actual render, so this is the only recovery
          // path for a challenge that was requested while no widget instance existed yet.
          if (executingRef.current) widgetRef.current?.execute();
        }}
        onBeforeInteractive={handleBeforeInteractive}
        onAfterInteractive={handleAfterInteractive}
        onTimeout={handleTimeout}
        onSuccess={handleSuccess}
        onError={handleError}
        onExpire={handleExpire}
      />
    </>
  ) : null;

  return { widget, getToken, reset, interactive };
}
